import { isBrowser, isFunction, isNotDefined } from '@enterprise/core';

import type { ElementTargetValue } from '#types/element-target-value.type';
import type { ElementTarget } from '#types/element-target.type';
import type { Target } from '#types/target.type';

/**
 * Retrieves the target element from a given target reference, function, or direct element.
 *
 * This utility handles different types of targets:
 * - If the target is a function, it calls the function to get the element.
 * - If the target is a ref object (with a `current` property), it returns the `current` value.
 * - Otherwise, it returns the target directly.
 *
 * If the code is not running in a browser environment, it returns `undefined`.
 * If the target is not defined, it returns the provided `defaultValue`.
 *
 * @template TElement - The type of the target element.
 * @param target - The target reference, function, or direct element.
 * @param defaultValue - Optional default value to return if the target is not defined.
 * @returns The resolved target element, or the default value if not available.
 */
export const getTargetElement = <TElement extends ElementTarget>(target: Target<TElement>, defaultValue?: TElement) => {
  if (!isBrowser) {
    return undefined;
  }

  if (isNotDefined(target)) {
    return defaultValue;
  }

  let targetElement: ElementTargetValue<TElement>;

  if (isFunction(target)) {
    targetElement = target();
  } else if ('current' in target) {
    targetElement = target.current;
  } else {
    targetElement = target;
  }

  return targetElement ?? defaultValue;
};
